/**
 * A very small validation helper.
 *
 * The plan file is the one artifact a human (or an agent) hand-writes, so
 * validation has to run offline in the CLI, in the server, and produce the same
 * machine-readable issue list in both places. A full schema engine would be more
 * than this needs; this is deliberately boring.
 */
export class ValidationError extends Error {
    issues;
    constructor(message, issues) {
        super(message);
        this.name = 'ValidationError';
        this.issues = issues;
    }
    get errors() {
        return this.issues.filter((issue) => issue.severity === 'error');
    }
}
export class Validator {
    issues = [];
    error(path, message) {
        this.issues.push({ path, message, severity: 'error' });
    }
    warn(path, message) {
        this.issues.push({ path, message, severity: 'warning' });
    }
    get collected() {
        return this.issues;
    }
    /** Required non-empty string. */
    string(value, path, opts = {}) {
        if (typeof value !== 'string') {
            this.error(path, `expected a string, received ${describe(value)}`);
            return undefined;
        }
        const trimmed = value.trim();
        const min = opts.min ?? 1;
        if (trimmed.length < min) {
            this.error(path, `must not be empty`);
            return undefined;
        }
        if (opts.max !== undefined && trimmed.length > opts.max) {
            this.error(path, `must be at most ${opts.max} characters (received ${trimmed.length})`);
            return undefined;
        }
        return trimmed;
    }
    enum(value, allowed, path) {
        if (typeof value !== 'string' || !allowed.includes(value)) {
            this.error(path, `must be one of: ${allowed.join(', ')}`);
            return undefined;
        }
        return value;
    }
    integer(value, path, opts = {}) {
        if (typeof value !== 'number' || !Number.isInteger(value)) {
            this.error(path, `expected an integer, received ${describe(value)}`);
            return undefined;
        }
        if (opts.min !== undefined && value < opts.min) {
            this.error(path, `must be >= ${opts.min}`);
            return undefined;
        }
        if (opts.max !== undefined && value > opts.max) {
            this.error(path, `must be <= ${opts.max}`);
            return undefined;
        }
        return value;
    }
    array(value, path, opts = {}) {
        if (!Array.isArray(value)) {
            this.error(path, `expected an array, received ${describe(value)}`);
            return undefined;
        }
        if (opts.min !== undefined && value.length < opts.min) {
            this.error(path, `must contain at least ${opts.min} item(s)`);
            return undefined;
        }
        if (opts.max !== undefined && value.length > opts.max) {
            this.error(path, `must contain at most ${opts.max} items (received ${value.length})`);
            return undefined;
        }
        return value;
    }
    unknownKeys(value, allowed, path) {
        for (const key of Object.keys(value)) {
            if (!allowed.includes(key)) {
                this.error(`${path}.${key}`, `unknown field; allowed: ${allowed.join(', ')}`);
            }
        }
    }
    /** Runs `fn`, converting any throw into an error issue rather than a crash. */
    guard(path, fn) {
        try {
            fn();
        }
        catch (error) {
            this.error(path, error instanceof Error ? error.message : String(error));
        }
    }
    throwIfFailed(message) {
        if (this.issues.some((issue) => issue.severity === 'error')) {
            throw new ValidationError(message, this.issues);
        }
    }
}
export function describe(value) {
    if (value === null)
        return 'null';
    if (Array.isArray(value))
        return 'an array';
    return typeof value;
}
//# sourceMappingURL=validate.js.map